import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Course, QuizResult } from '../types';
import { courseService } from '../services/courseService';
import { quizService } from '../services/quizService';

const PROGRESS_KEY_PREFIX = 'carelms:lesson-progress:';
const progressKey = (courseId: string, lessonId: string) =>
    `${PROGRESS_KEY_PREFIX}${courseId}:${lessonId}`;

export function useCourseView() {
    const location = useLocation();
    const navigate = useNavigate();
    
    // Core state
    const [course, setCourse] = useState<Course | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
    const [played, setPlayed] = useState(0);
    const [maxPlayed, setMaxPlayed] = useState(0);
    const [videoDuration, setVideoDuration] = useState(0);
    const [initialPlayedSeconds, setInitialPlayedSeconds] = useState(0);
    const lastSaveAtRef = useRef(0);
    
    // Completion state
    const [isCompleted, setIsCompleted] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    
    // Quiz state
    const [showQuiz, setShowQuiz] = useState(false);
    const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
    const [showQuizResults, setShowQuizResults] = useState(false);
    const [quizCompletionStatus, setQuizCompletionStatus] = useState<{
        hasQuiz: boolean;
        isCompleted: boolean;
        score?: number;
    } | null>(null);
    const [checkingQuizStatus, setCheckingQuizStatus] = useState(false);
    const [isCompletingCourse, setIsCompletingCourse] = useState(false);
    const [hasCheckedQuizStatus, setHasCheckedQuizStatus] = useState(false);

    // Get course ID from URL
    const courseId = location.pathname.split('/').pop();

    // Fetch course details
    useEffect(() => {
        const fetchCourseDetails = async () => {
            if (courseId) {
                try {
                    setIsLoading(true);
                    const courseData = await courseService.getCourseDetails(courseId);
                    setCourse(courseData);
                } catch (error) {
                    console.error('Error fetching course details:', error);
                    navigate('/courses');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        
        fetchCourseDetails();
    }, [courseId, navigate]);

    // Handle location state changes (when course is updated after lesson completion)
    useEffect(() => {
        if (location.state?.course) {
            setCourse(location.state.course);
        }
    }, [location.state]);

    // Set initial lesson index and check completion status
    useEffect(() => {
        if (course) {
            const firstUncompleted = course.lessons.findIndex(lesson => !lesson.completed);
            setCurrentLessonIndex(firstUncompleted === -1 ? 0 : firstUncompleted);
            setIsCompleted(course.status === 'completed');
            setHasCheckedQuizStatus(false);
        }
    }, [course]);

    // Check quiz completion status when all lessons are completed
    useEffect(() => {
        if (course && isCompleted && !isCompletingCourse && !hasCheckedQuizStatus) {
            const checkQuizCompletionStatus = async () => {
                try {
                    setCheckingQuizStatus(true);
                    setHasCheckedQuizStatus(true);
                    
                    // First, check if there are any quiz questions for this course
                    const questions = await quizService.getQuizQuestions(course.id);
                    
                    if (questions.length === 0) {
                        // No quiz, mark course as completed
                        setIsCompletingCourse(true);
                        await courseService.completeCourse(course.id);
                        setIsCompleted(true);
                        setQuizCompletionStatus({ hasQuiz: false, isCompleted: true });
                        setIsCompletingCourse(false);
                    } else {
                        // There is a quiz, check if it's already completed
                        const status = await quizService.getQuizCompletionStatus(course.id);
                        setQuizCompletionStatus(status);
                        
                        if (status.isCompleted) {
                            // Quiz already completed, mark course as completed
                            setIsCompletingCourse(true);
                            await courseService.completeCourse(course.id);
                            setIsCompleted(true);
                            setIsCompletingCourse(false);
                        } else {
                            // Quiz not completed, show quiz
                            setShowQuiz(true);
                        }
                    }
                } catch (error) {
                    console.error('Error checking quiz completion status:', error);
                } finally {
                    setCheckingQuizStatus(false);
                }
            };
            
            checkQuizCompletionStatus();
        }
    }, [course, isCompleted, isCompletingCourse, hasCheckedQuizStatus]);

    // Calculate derived state
    const currentLesson = course?.lessons?.[currentLessonIndex];
    const firstUncompletedIndex = course?.lessons?.findIndex(lesson => !lesson.completed) ?? -1;
    const activeLessonForLocking = firstUncompletedIndex === -1 ? (course?.lessons?.length ?? 0) - 1 : firstUncompletedIndex;
    const canMarkComplete = played >= 0.95 && !isCompleted;

    // Restore persisted progress for the current lesson when it changes.
    useEffect(() => {
        if (!course?.id || !currentLesson?.id) return;
        try {
            const raw = localStorage.getItem(progressKey(course.id, currentLesson.id));
            if (!raw) {
                setInitialPlayedSeconds(0);
                setPlayed(0);
                setMaxPlayed(0);
                return;
            }
            const saved = JSON.parse(raw) as { playedSeconds?: number; played?: number };
            setInitialPlayedSeconds(saved.playedSeconds ?? 0);
            if (typeof saved.played === 'number') {
                setPlayed(saved.played);
                setMaxPlayed(saved.played);
            }
        } catch {
            // Ignore corrupt localStorage entries
            setInitialPlayedSeconds(0);
        }
    }, [course?.id, currentLesson?.id]);

    // Progress tracking with throttled auto-save to localStorage.
    const handleProgress = (newPlayed: number, newMaxPlayed: number) => {
        setPlayed(newPlayed);
        setMaxPlayed(newMaxPlayed);

        if (!course?.id || !currentLesson?.id) return;
        const now = Date.now();
        if (now - lastSaveAtRef.current < 2000) return;
        lastSaveAtRef.current = now;
        try {
            localStorage.setItem(
                progressKey(course.id, currentLesson.id),
                JSON.stringify({
                    played: newPlayed,
                    playedSeconds: videoDuration ? newPlayed * videoDuration : 0,
                    updatedAt: now
                })
            );
        } catch {
            // localStorage may throw in private mode / quota exceeded - safe to ignore
        }
    };

    // Persist on tab close as a safety net.
    useEffect(() => {
        if (!course?.id || !currentLesson?.id) return;
        const handler = () => {
            try {
                localStorage.setItem(
                    progressKey(course.id, currentLesson.id),
                    JSON.stringify({
                        played,
                        playedSeconds: videoDuration ? played * videoDuration : 0,
                        updatedAt: Date.now()
                    })
                );
            } catch {
                // ignore
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [course?.id, currentLesson?.id, played, videoDuration]);

    const clearPersistedProgress = (courseIdLocal: string, lessonIdLocal: string) => {
        try {
            localStorage.removeItem(progressKey(courseIdLocal, lessonIdLocal));
        } catch {
            // ignore
        }
    };

    // Lesson navigation
    const handleSelectLesson = (index: number) => {
        if (index <= activeLessonForLocking || course?.lessons[index]?.completed) {
            setCurrentLessonIndex(index);
            setPlayed(0);
            setMaxPlayed(0);
            setInitialPlayedSeconds(0);
        }
    };

    const handlePreviousLesson = () => {
        const prevIndex = currentLessonIndex - 1;
        if (prevIndex >= 0) {
            setCurrentLessonIndex(prevIndex);
            setPlayed(0);
            setMaxPlayed(0);
            setInitialPlayedSeconds(0);
        }
    };

    const handleNextLesson = () => {
        const nextIndex = currentLessonIndex + 1;
        if (nextIndex < (course?.lessons.length ?? 0) && (nextIndex <= activeLessonForLocking || course?.lessons[nextIndex]?.completed)) {
            setCurrentLessonIndex(nextIndex);
            setPlayed(0);
            setMaxPlayed(0);
            setInitialPlayedSeconds(0);
        }
    };

    // Lesson completion
    const handleMarkComplete = async () => {
        if (!canMarkComplete) return;

        try {
            setIsUpdating(true);
            await courseService.updateLessonCompletion(course?.id || '', currentLesson?.id || '', true);
            if (course?.id && currentLesson?.id) {
                clearPersistedProgress(course.id, currentLesson.id);
            }
            
            if (course && currentLesson) {
                const updatedLessons = course.lessons.map(lesson => 
                    lesson.id === currentLesson.id ? { ...lesson, completed: true } : lesson
                );
                const allLessonsCompleted = updatedLessons.every(lesson => lesson.completed);
                
                const updatedCourse = {
                    ...course,
                    lessons: updatedLessons,
                    status: allLessonsCompleted ? 'inProgress' : course.status,
                    completionDate: course.completionDate,
                };
                setIsCompleted(false);
                
                navigate(location.pathname, { state: { course: updatedCourse } });

                if (allLessonsCompleted) {
                    setShowQuiz(true);
                }
            }
        } catch (error) {
            console.error('Error marking lesson as complete:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    // Quiz handlers
    const handleQuizComplete = async (result: QuizResult) => {
        setQuizResult(result);
        setShowQuiz(false);
        setShowQuizResults(true);
        
        if (result.passed) {
            try {
                setIsCompletingCourse(true);
                await courseService.completeCourse(course?.id || '');
                setIsCompleted(true);
            } catch (error) {
                console.error('Error completing course:', error);
            } finally {
                setIsCompletingCourse(false);
            }
        }
    };

    const handleQuizRetake = () => {
        setShowQuizResults(false);
        setShowQuiz(true);
    };

    const handleQuizContinue = () => {
        setShowQuizResults(false);
        navigate('/courses');
    };

    const handleBackToCourse = () => {
        setShowQuiz(false);
        setShowQuizResults(false);
        setQuizResult(null);
    };

    return {
        // State
        course,
        isLoading,
        currentLessonIndex,
        played,
        maxPlayed,
        initialPlayedSeconds,
        setVideoDuration,
        isCompleted,
        isUpdating,
        showQuiz,
        quizResult,
        showQuizResults,
        quizCompletionStatus,
        checkingQuizStatus,
        isCompletingCourse,
        hasCheckedQuizStatus,
        
        // Derived state
        currentLesson,
        activeLessonForLocking,
        canMarkComplete,
        
        // Handlers
        handleProgress,
        handleSelectLesson,
        handlePreviousLesson,
        handleNextLesson,
        handleMarkComplete,
        handleQuizComplete,
        handleQuizRetake,
        handleQuizContinue,
        handleBackToCourse,
    };
}
